import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import LegislationPickerDialog, {
  SelectedLegislation,
} from "@/pages/dashboard/config/LegislationPickerDialog";
import useLegislation from "@/hooks/useLegislation";
import {
  useCreateConsolidationPriority,
  useUpdateConsolidationPriority,
} from "@/hooks/useConsolidation";
import { useConsolidationTimelineMetadata } from "@/hooks/useConsolidationTimelineMetadata";
import CompactTimeline from "@/partials/document-renderer/components/document-timeline/CompactTimeline";
import { normalizeTimelineItems } from "@/lib/bekTimelineNormalization";
import { flattenConsolidationTimeline } from "@/lib/consolidationViewerUtils";
import { flattenTimeline } from "@/lib/diffViewHtml";
import {
  ConsolidationPriority,
  ConsolidationPriorityMode,
} from "@/models/consolidation";
import { TimelineItem } from "@/models/legislation/Timeline";
import { logger } from "@/lib/logger";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { MODE_DESCRIPTIONS, MODE_LABELS } from "./consolidationPriorityDisplay";
import {
  assignWindows,
  buildTimelineRoot,
  coversFutureDocuments,
  getCoveredAccessions,
  getValidWindowIndexes,
  isWindowAnchor,
} from "./priorityTimeline";

interface PrioritizeLawDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Skips the law-picker step (e.g. launched from the Kilder overview). */
  initialLaw?: SelectedLegislation | null;
  /** Edit mode: repoint an existing prioritization (law cannot change). */
  existingPriority?: ConsolidationPriority | null;
}

/**
 * Create or edit a consolidation prioritization. Create flow: pick a law via
 * the shared legislation picker (unless one is provided), then pick the pivot
 * window anchor on the SAME timeline the Retsinformation document view shows
 * (type-colored nodes, consolidation diamonds, legend, date navigator) plus
 * the scope mode — the covered documents are washed on the timeline.
 */
const PrioritizeLawDialog: FC<PrioritizeLawDialogProps> = ({
  open,
  onOpenChange,
  initialLaw = null,
  existingPriority = null,
}) => {
  const isEdit = existingPriority !== null;
  const presetLaw: SelectedLegislation | null = useMemo(() => {
    if (existingPriority) {
      return {
        externalId: existingPriority.lawExternalId,
        datasource: "Retsinformation",
        title: existingPriority.lawTitle ?? existingPriority.lawExternalId,
      };
    }
    return initialLaw;
  }, [existingPriority, initialLaw]);

  const [pickedLaw, setPickedLaw] = useState<SelectedLegislation | null>(null);
  const selectedLaw = presetLaw ?? pickedLaw;

  const [pivotExternalId, setPivotExternalId] = useState<string | null>(null);
  const [mode, setMode] = useState<ConsolidationPriorityMode>("WINDOW");
  const [level, setLevel] = useState(3);

  // Prefill pivot/mode/level when (re)opening in edit mode.
  useEffect(() => {
    if (!open) return;
    setPivotExternalId(existingPriority?.pivotExternalId ?? null);
    setMode(existingPriority?.mode ?? "WINDOW");
    setLevel(existingPriority?.level ?? 3);
  }, [open, existingPriority]);

  const { useGetTimelineByExternalId } = useLegislation();
  const timelineQuery = useGetTimelineByExternalId(
    selectedLaw?.externalId ?? "",
    { enabled: open && Boolean(selectedLaw) }
  );
  const createPriority = useCreateConsolidationPriority();
  const updatePriority = useUpdateConsolidationPriority();

  const normalizedItems = useMemo(
    () => normalizeTimelineItems(timelineQuery.data ?? []),
    [timelineQuery.data]
  );
  const timelineRoot = useMemo(
    () => buildTimelineRoot(normalizedItems),
    [normalizedItems]
  );
  // Same order the timeline strip renders, so anchor/window positions line up.
  const flatItems: TimelineItem[] = useMemo(
    () => (timelineRoot ? flattenTimeline([timelineRoot]) : []),
    [timelineRoot]
  );
  // Window math runs on the consolidation-scoped flatten (the same helper the
  // viewer's counts use, mirroring the API): BEK families keep only 60/80
  // nodes, deduped, so nested Lov references never open or fill a window and
  // the picker agrees with the enqueue. The strip still renders the full tree
  // (flatItems); coverage washes join by accession.
  const windowItems = useMemo(
    () => flattenConsolidationTimeline(normalizedItems),
    [normalizedItems]
  );
  const windowEntries = useMemo(
    () => assignWindows(windowItems),
    [windowItems]
  );
  const validWindowIndexes = useMemo(
    () => getValidWindowIndexes(windowEntries),
    [windowEntries]
  );
  const coveredAccessions = useMemo(
    () => getCoveredAccessions(windowEntries, pivotExternalId, mode),
    [windowEntries, pivotExternalId, mode]
  );

  /**
   * The window anchor a document resolves to as pivot: itself when it IS an
   * anchor (LBK/BEK anywhere, or the original hovedlov — a later LOVH is a
   * change law, not an anchor), otherwise the anchor of the window it belongs
   * to. Null when the accession is not on the timeline, or when its window is
   * not consolidatable (anchor before 2016, or no change laws yet) — the
   * consolidation solution never enqueues such windows, so they cannot be
   * prioritized.
   */
  const resolveAnchorAccession = (accessionNumber: string): string | null => {
    const index = windowItems.findIndex(
      (item) => item.accessionNumber === accessionNumber
    );
    if (index < 0) return null;
    const entry = windowEntries[index];
    if (!validWindowIndexes.has(entry.windowIndex)) return null;
    if (isWindowAnchor(windowItems[index], index)) return accessionNumber;
    const anchor = windowEntries.find(
      (e) => e.isAnchor && e.windowIndex === entry.windowIndex
    );
    return anchor?.doc.accessionNumber ?? null;
  };

  // Preselect the document chosen in the law picker (or the Kilder overview)
  // as the pivot once the timeline has loaded, so its scope is highlighted
  // immediately instead of starting with nothing selected.
  useEffect(() => {
    if (!open || isEdit || pivotExternalId) return;
    if (!selectedLaw?.externalId || flatItems.length === 0) return;
    const resolved = resolveAnchorAccession(selectedLaw.externalId);
    if (resolved) setPivotExternalId(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, pivotExternalId, selectedLaw?.externalId, flatItems]);

  // The lineage root the backend keys on (timeline_gold[0]).
  const lawExternalId =
    flatItems[0]?.accessionNumber ?? selectedLaw?.externalId ?? null;
  // TimelineStrip resolves its nodes from the "current" accession's ancestor
  // path and renders NOTHING when it matches no node — so before a pivot is
  // chosen, focus the strip on the lineage root.
  const timelineFocusAccession =
    pivotExternalId ?? flatItems[0]?.accessionNumber ?? "";
  const pivotDoc = flatItems.find(
    (doc) => doc.accessionNumber === pivotExternalId
  );

  // Same per-window consolidation status the document timeline's diamonds use.
  const windowMetadataQuery = useConsolidationTimelineMetadata({
    rootExternalId: lawExternalId ?? undefined,
    timelineFallbackExternalId: selectedLaw?.externalId,
    enabled: open && Boolean(selectedLaw),
  });

  const reset = () => {
    setPickedLaw(null);
    setPivotExternalId(null);
    setMode("WINDOW");
    setLevel(3);
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  // The picker confirms by calling onSelect(...) and then immediately
  // onOpenChange(false) to close itself (LegislationPickerDialog.handleConfirm).
  // Track the selection in a ref so that trailing close is treated as
  // "advance to step 2", not as a cancel of the whole flow.
  const selectionMadeRef = useRef(false);

  const handleLawSelected = (documents: SelectedLegislation[]) => {
    if (documents.length === 0) return;
    selectionMadeRef.current = true;
    setPickedLaw(documents[0]);
    setPivotExternalId(null);
  };

  const handlePickerOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    if (selectionMadeRef.current) {
      selectionMadeRef.current = false;
      return;
    }
    handleClose(false);
  };

  // A change law is not a valid pivot: clicking one selects its window's anchor.
  const selectPivotFromNode = (accessionNumber: string) => {
    const resolved = resolveAnchorAccession(accessionNumber);
    if (resolved) setPivotExternalId(resolved);
  };

  const handleSubmit = async () => {
    if (!selectedLaw || !lawExternalId || !pivotExternalId) return;
    try {
      if (isEdit && existingPriority) {
        await updatePriority.mutateAsync({
          id: existingPriority.id,
          pivotExternalId,
          mode,
          level,
          pivotDocumentTypeId: pivotDoc?.documentTypeId,
        });
        showSuccessToast("Prioriteringen er opdateret.");
      } else {
        await createPriority.mutateAsync({
          lawExternalId,
          pivotExternalId,
          mode,
          level,
          lawTitle: selectedLaw.title,
          pivotDocumentTypeId: pivotDoc?.documentTypeId,
        });
        showSuccessToast("Loven er prioriteret til konsolidering.");
      }
      handleClose(false);
    } catch (error) {
      logger.error("Failed to save consolidation priority", {
        operation: isEdit
          ? "updateConsolidationPriorityFromDialog"
          : "prioritizeLawForConsolidation",
        error,
        priorityId: existingPriority?.id,
        lawExternalId,
        pivotExternalId,
        mode,
      });
      showErrorToast(
        "Kunne ikke gemme prioriteringen. Den kan allerede findes, eller dokumentet har endnu ingen konsolideringsvindue."
      );
    }
  };

  const isSaving = createPriority.isPending || updatePriority.isPending;

  // Step 1: the shared law picker (create flow without a preset law).
  if (open && !selectedLaw) {
    return (
      <LegislationPickerDialog
        open={open}
        onOpenChange={handlePickerOpenChange}
        onSelect={handleLawSelected}
        showConsolidationStatus
        onlyConsolidationEligible
      />
    );
  }

  // Step 2: pivot window anchor + scope on the real document timeline.
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {/* sm: prefix required: DialogContent's own sm:max-w-md wins over an
          unprefixed max-w-* (different tailwind-merge variant groups). */}
      <DialogContent size="full">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Rediger prioritering" : "Prioriter konsolidering"}
          </DialogTitle>
          <DialogDescription>
            {selectedLaw?.title} — vælg et vinduesanker (LBK, BEK eller
            hovedlov) på tidslinjen og omfanget af prioriteringen. Fremhævede
            dokumenter er omfattet; kun vinduer med ændringslove og anker fra{" "}
            2016 og frem kan konsolideres.
          </DialogDescription>
        </DialogHeader>

        {timelineQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : !timelineRoot || flatItems.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            Der blev ikke fundet en tidslinje for denne lov.
          </p>
        ) : (
          // min-w-0 is load-bearing: DialogContent is a grid, and a grid
          // item's default min-width:auto lets the timeline strip's intrinsic
          // width push content past the dialog instead of scrolling inside
          // its overflow-x container.
          <div className="flex min-w-0 flex-col gap-4">
            <div className="min-w-0 rounded-md border p-2">
              <CompactTimeline
                root={timelineRoot}
                currentAccessionNumber={timelineFocusAccession}
                timelineSelection={
                  pivotExternalId
                    ? { kind: "silver", accessionNumber: pivotExternalId }
                    : null
                }
                datasource={selectedLaw?.datasource ?? "Retsinformation"}
                onNavigate={(accessionNumber) =>
                  selectPivotFromNode(accessionNumber)
                }
                inboundByAnchor={windowMetadataQuery.data?.byAnchor}
                showConsolidationDiamonds
                onOpenConsolidation={(changelawAccession) =>
                  selectPivotFromNode(changelawAccession)
                }
                highlightedAccessions={coveredAccessions}
              />
            </div>

            <div>
              <Label className="mb-2 block">Omfang</Label>
              <RadioGroup
                value={mode}
                onValueChange={(value) =>
                  setMode(value as ConsolidationPriorityMode)
                }
                className="gap-3"
              >
                {(Object.keys(MODE_LABELS) as ConsolidationPriorityMode[]).map(
                  (value) => (
                    <div key={value} className="flex items-start gap-2">
                      <RadioGroupItem
                        value={value}
                        id={`priority-mode-${value}`}
                        className="mt-0.5"
                      />
                      <Label
                        htmlFor={`priority-mode-${value}`}
                        className="flex min-w-0 cursor-pointer flex-col items-start gap-0.5 text-left font-normal"
                      >
                        <span className="font-medium text-foreground">
                          {MODE_LABELS[value]}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {MODE_DESCRIPTIONS[value]}
                        </span>
                      </Label>
                    </div>
                  )
                )}
              </RadioGroup>
              {coversFutureDocuments(mode) && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Fremtidige ændringslove og LBK&apos;er bliver også omfattet og
                  konsolideres automatisk.
                </p>
              )}
            </div>

            <div>
              <Label className="mb-2 block">Prioritet</Label>
              <RadioGroup
                value={String(level)}
                onValueChange={(value) => setLevel(Number(value))}
                className="flex flex-row gap-4"
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <div key={value} className="flex items-center gap-1.5">
                    <RadioGroupItem
                      value={String(value)}
                      id={`priority-level-${value}`}
                    />
                    <Label
                      htmlFor={`priority-level-${value}`}
                      className="cursor-pointer font-normal text-foreground"
                    >
                      {value}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <p className="mt-1.5 text-xs text-muted-foreground">
                1 er højeste prioritet; højere prioriteter konsolideres først.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!isEdit && !initialLaw && (
            <Button variant="outline" onClick={() => setPickedLaw(null)}>
              Vælg anden lov
            </Button>
          )}
          <Button
            onClick={() => void handleSubmit()}
            disabled={!pivotExternalId || !lawExternalId || isSaving}
          >
            {isSaving ? "Gemmer…" : isEdit ? "Gem" : "Prioriter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrioritizeLawDialog;
